import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { SmilePlus, Copy, Check, Reply, Pencil, Loader2 } from 'lucide-react';
import { REACTIONS } from '../../types';
import ChunkBoundary from '../ChunkBoundary';

// The full picker (Emoji+), loaded only when someone opens it.
const FullEmojiPicker = lazy(() => import('./FullEmojiPicker'));

type Props = {
  isUser: boolean;
  isNight: boolean;
  reactions?: { mine?: string; theirs?: string };
  // Reactions only work in a live human chat.
  canReact: boolean;
  // Text to copy from the menu (text messages only).
  copyText?: string;
  // Double-tap a text message to send a heart, like Instagram.
  doubleTapHeart?: boolean;
  onReact: (emoji: string | null) => void;
  onCopy?: (text: string) => Promise<boolean>;
  // Reply to this message (swipe right on a phone, or from the menu).
  onReply?: () => void;
  // Edit your own text message (within 15 minutes; the parent decides).
  onEdit?: () => void;
  children: React.ReactNode;
};

const LONG_PRESS_MS = 450;
const SWIPE_REPLY_PX = 56; // how far to swipe right to reply
const SWIPE_MAX_PX = 72;

// Wraps one chat bubble: hover (desktop) or long-press (phone) opens a small bar
// of reactions. The chosen reaction sits on the corner of the bubble.
export default function ReactionWrap({ isUser, isNight, reactions, canReact, copyText, doubleTapHeart, onReact, onCopy, onReply, onEdit, children }: Props) {
  const [openState, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [moreEmojis, setMoreEmojis] = useState(false); // the full picker instead of the quick bar
  const rootRef = useRef<HTMLDivElement>(null);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressStart = useRef<{ x: number; y: number } | null>(null);
  const longPressed = useRef(false);
  const lastTap = useRef(0);
  const lastPointer = useRef<string>('mouse');
  // Open the bar below the bubble when there is no room above it (e.g. a tall GIF at the top).
  const [below, setBelow] = useState(false);
  // Swipe right to reply: how far the bubble follows the finger.
  const [swipeX, setSwipeX] = useState(0);
  const swiping = useRef(false);
  const swipeFrom = useRef<{ x: number; y: number } | null>(null);
  const menuEnabled = canReact || !!copyText || !!onReply || !!onEdit;
  const open = openState && menuEnabled; // closes on its own when the chat ends

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);


  const clearPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = null;
    pressStart.current = null;
  };

  const openMenu = () => {
    const root = rootRef.current;
    if (root) {
      let scroller: HTMLElement | null = root.parentElement;
      while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
      const top = scroller ? scroller.getBoundingClientRect().top : 0;
      setBelow(root.getBoundingClientRect().top - top < 56);
    }
    setMoreEmojis(false); // always opens on the quick reactions
    setOpen(true);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    lastPointer.current = e.pointerType;
    if (e.pointerType === 'mouse' || !menuEnabled) return;
    clearPress(); // a second finger must not leave the first timer running
    longPressed.current = false;
    pressStart.current = { x: e.clientX, y: e.clientY };
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      navigator.vibrate?.(10);
      openMenu();
    }, LONG_PRESS_MS);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = swiping.current ? swipeFrom.current : pressStart.current;
    if (!s) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (!swiping.current && Math.hypot(dx, dy) > 8) {
      clearPress(); // moving: not a long-press
      // A mostly sideways move to the right starts a swipe-to-reply.
      if (onReply && e.pointerType !== 'mouse' && dx > 0 && Math.abs(dy) < dx * 0.6) { swiping.current = true; swipeFrom.current = s; }
    }
    if (swiping.current) setSwipeX(Math.max(0, Math.min(dx, SWIPE_MAX_PX)));
  };
  const resetSwipe = () => { swiping.current = false; swipeFrom.current = null; setSwipeX(0); };
  const onPointerUp = (e: React.PointerEvent) => {
    const wasSwipe = swiping.current;
    if (wasSwipe && swipeX >= SWIPE_REPLY_PX) { navigator.vibrate?.(10); onReply?.(); }
    resetSwipe();
    clearPress();
    if (wasSwipe || e.pointerType === 'mouse' || longPressed.current) return;
    if (!doubleTapHeart || !canReact) return;
    const now = Date.now();
    if (now - lastTap.current < 300) {
      lastTap.current = 0;
      onReact(reactions?.mine === '❤️' ? null : '❤️');
    } else {
      lastTap.current = now;
    }
  };
  // After a long-press, don't also "tap" what's inside (e.g. reveal a photo).
  const onClickCapture = (e: React.MouseEvent) => {
    if (longPressed.current) {
      e.preventDefault();
      e.stopPropagation();
      longPressed.current = false;
    }
  };

  // On Android the browser's own long-press (about 400ms) fires `contextmenu` and,
  // on images and GIFs, cancels the pointer before our timer runs. Treat that
  // touch `contextmenu` as the long-press: block the native menu and open ours.
  // A mouse right-click keeps the normal browser menu.
  const onContextMenu = (e: React.MouseEvent) => {
    if (!menuEnabled || lastPointer.current === 'mouse') return;
    e.preventDefault();
    clearPress();
    if (!longPressed.current) {
      longPressed.current = true;
      navigator.vibrate?.(10);
      openMenu();
    }
  };

  const pick = (emoji: string) => {
    onReact(reactions?.mine === emoji ? null : emoji);
    setOpen(false);
  };

  const chips = [reactions?.theirs, reactions?.mine].filter(Boolean) as string[];
  const same = chips.length === 2 && chips[0] === chips[1];
  const shown = same ? [chips[0]] : chips;

  return (
    <div
      ref={rootRef}
      className={`relative group flex items-center gap-1 max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'} ${shown.length ? 'mb-3' : ''}`}
    >
      <div
        className="relative min-w-0 max-w-full [@media(hover:none)]:select-none [-webkit-touch-callout:none] [&_img]:[-webkit-user-drag:none]"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { clearPress(); resetSwipe(); }}
        style={{ transform: swipeX ? `translateX(${swipeX}px)` : undefined, transition: swipeX ? 'none' : 'transform 0.2s ease', touchAction: onReply ? 'pan-y' : undefined }}
        onClickCapture={onClickCapture}
        onContextMenu={onContextMenu}
      >
        {children}

        {/* Reply arrow that fades in while swiping */}
        {swipeX > 0 && (
          <span aria-hidden className="absolute top-1/2 -left-8 flex items-center justify-center w-6 h-6 rounded-full"
            style={{ opacity: Math.min(1, swipeX / SWIPE_REPLY_PX), transform: `translateY(-50%) scale(${swipeX >= SWIPE_REPLY_PX ? 1.15 : 0.9})` }}>
            <Reply size={16} />
          </span>
        )}

        {shown.length > 0 && (
          <button
            type="button"
            onClick={() => menuEnabled && setOpen(o => !o)}
            aria-label={`Reactions: ${chips.join(' ')}`}
            className={`absolute -bottom-3 ${isUser ? 'right-2' : 'left-2'} flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[0.75rem] leading-none border shadow-sm ${
              isNight ? 'bg-[#1f1f1f] border-white/10' : 'bg-white border-black/10'
            }`}
          >
            {shown.map(r => <span key={r}>{r}</span>)}
            {same && <span className="text-[0.625rem] opacity-60 ml-0.5">2</span>}
          </button>
        )}
      </div>

      {/* Desktop: small buttons next to the bubble, shown on hover or focus. */}
      {onReply && (
        <button
          type="button"
          onClick={onReply}
          aria-label="Reply to this message"
          className={`shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-50 hover:!opacity-100 focus-visible:opacity-100 transition-opacity [@media(hover:none)]:hidden ${
            isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'
          }`}
        >
          <Reply size={15} />
        </button>
      )}
      {/* Edit your own text (desktop). On a phone it is in the long-press menu. */}
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label="Edit this message"
          className={`shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-50 hover:!opacity-100 focus-visible:opacity-100 transition-opacity [@media(hover:none)]:hidden ${
            isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'
          }`}
        >
          <Pencil size={14} />
        </button>
      )}
      {(canReact || !!copyText || !!onEdit) && (
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : openMenu())}
          aria-label="React to this message"
          aria-expanded={open}
          className={`shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-50 hover:!opacity-100 focus-visible:opacity-100 transition-opacity [@media(hover:none)]:hidden ${
            isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'
          }`}
        >
          <SmilePlus size={15} />
        </button>
      )}

      {open && (
        <div
          role="menu"
          aria-label="React"
          className={`absolute z-30 ${below ? 'top-full mt-1.5' : 'bottom-full mb-1.5'} ${isUser ? 'right-0' : 'left-0'} flex items-center gap-0.5 p-1 ${moreEmojis ? 'rounded-2xl' : 'rounded-full'} border shadow-xl ${
            isNight ? 'bg-[#1f1f1f] border-white/10' : 'bg-white border-black/10'
          }`}
        >
          {canReact && moreEmojis && (
            <div className="w-[min(18rem,calc(100vw-3rem))] p-2" onPointerDown={e => e.stopPropagation()}>
              <ChunkBoundary fallback={<p className="p-4 text-center text-xs opacity-70">Couldn't load more emojis. Refresh the page after your chat.</p>}>
                <Suspense fallback={<div className="h-[min(340px,45dvh)] flex items-center justify-center"><Loader2 className="animate-spin opacity-50" size={20} /></div>}>
                  <FullEmojiPicker isNight={isNight} onPick={pick} onBack={() => setMoreEmojis(false)} />
                </Suspense>
              </ChunkBoundary>
            </div>
          )}
          {canReact && !moreEmojis && REACTIONS.map(r => (
            <button
              key={r}
              type="button"
              role="menuitem"
              onClick={() => pick(r)}
              aria-label={`React ${r}`}
              aria-pressed={reactions?.mine === r}
              className={`w-9 h-9 flex items-center justify-center rounded-full text-[1.25rem] transition-transform hover:scale-125 active:scale-95 ${
                reactions?.mine === r ? (isNight ? 'bg-white/15' : 'bg-black/10') : ''
              }`}
            >
              {r}
            </button>
          ))}
          {/* Emoji+: any emoji, from the full picker. Your current one shows here if it is not a quick one. */}
          {canReact && !moreEmojis && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                // The picker is tall: open it on the side of the bubble with more room.
                const root = rootRef.current;
                let scroller: HTMLElement | null = root?.parentElement ?? null;
                while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
                if (root && scroller) {
                  const box = root.getBoundingClientRect(), view = scroller.getBoundingClientRect();
                  setBelow(view.bottom - box.bottom > box.top - view.top);
                }
                setMoreEmojis(true);
              }}
              aria-label={reactions?.mine && !(REACTIONS as readonly string[]).includes(reactions.mine) ? `More emojis (you reacted ${reactions.mine})` : 'More emojis'}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-transform hover:scale-110 ${
                reactions?.mine && !(REACTIONS as readonly string[]).includes(reactions.mine) ? `text-[1.25rem] ${isNight ? 'bg-white/15' : 'bg-black/10'}` : 'opacity-70 hover:opacity-100'
              }`}
            >
              {reactions?.mine && !(REACTIONS as readonly string[]).includes(reactions.mine) ? reactions.mine : <SmilePlus size={18} />}
            </button>
          )}
          {onReply && !moreEmojis && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onReply(); }}
              aria-label="Reply"
              className={`w-9 h-9 flex items-center justify-center rounded-full opacity-70 hover:opacity-100 ${canReact ? `border-l ${isNight ? 'border-white/10' : 'border-black/10'} rounded-l-none` : ''}`}
            >
              <Reply size={16} />
            </button>
          )}
          {onEdit && !moreEmojis && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); onEdit(); }}
              aria-label="Edit message"
              className={`w-9 h-9 flex items-center justify-center rounded-full opacity-70 hover:opacity-100 border-l ${isNight ? 'border-white/10' : 'border-black/10'} rounded-l-none`}
            >
              <Pencil size={15} />
            </button>
          )}
          {copyText && onCopy && !moreEmojis && (
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                if (await onCopy(copyText)) {
                  setCopied(true);
                  setTimeout(() => { setCopied(false); setOpen(false); }, 900);
                }
              }}
              aria-label="Copy message"
              className={`w-9 h-9 flex items-center justify-center rounded-full opacity-70 hover:opacity-100 ${canReact || onReply ? `border-l ${isNight ? 'border-white/10' : 'border-black/10'} rounded-l-none` : ''}`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
